import React from 'react';

export interface SeoIssue {
  factor: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  points_lost: number;
}

export interface PageData {
  h1: string;
  h2s: string[];
  image_count: number;
  images_with_alt: number;
  word_count: number;
  internal_links: number;
  has_canonical: boolean;
  has_schema: boolean;
  has_og: boolean;
  has_twitter: boolean;
  has_viewport: boolean;
}

export interface AiSuggestions {
  improved_title: string;
  improved_meta: string;
  improved_h1: string;
  content_tips: string[];
  ai_search_tips: string[];
}

export interface SeoResult {
  id: number;
  scan_id: number;
  asset_id: number;
  url: string;
  page_title: string;
  meta_description: string;
  seo_score: number;
  keyword_score: number;
  status: 'pending' | 'scanning' | 'completed' | 'failed';
  issues: SeoIssue[];
  page_data: PageData | null;
  ai_suggestions: AiSuggestions | null;
  ai_generated_at: string | null;
  error_message: string | null;
  scanned_at: string | null;
}

export interface SeoScan {
  id: number;
  asset_id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  total_pages: number;
  scanned_pages: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

// ─── Shared UI components ─────────────────────────────────────────────────────

export const ScoreBadge: React.FC<{ score: number; size?: 'sm' | 'md' | 'lg' }> = ({ score, size = 'md' }) => {
  const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const bg = score >= 80 ? '#d1fae5' : score >= 50 ? '#fef3c7' : '#fee2e2';
  const sizes = {
    sm: { font: '0.68rem', pad: '2px 6px' },
    md: { font: '0.8rem', pad: '3px 8px' },
    lg: { font: '1rem', pad: '4px 12px' },
  };
  return (
    <span style={{ background: bg, color, fontWeight: 700, borderRadius: 6, fontSize: sizes[size].font, padding: sizes[size].pad, whiteSpace: 'nowrap' as const }}>
      {score}/100
    </span>
  );
};

export const SeverityBadge: React.FC<{ severity: string; count: number; label?: string }> = ({ severity, count, label }) => {
  const map: Record<string, { bg: string; color: string }> = {
    critical: { bg: '#fee2e2', color: '#dc2626' },
    warning: { bg: '#fef3c7', color: '#d97706' },
    info: { bg: '#dbeafe', color: '#2563eb' },
  };
  const s = map[severity] || map.info;
  if (!count) return null;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px', borderRadius: 6 }}>
      {count} {label || severity}
    </span>
  );
};

export const shortUrl = (url: string, max = 55) => {
  const s = url.replace(/^https?:\/\/(www\.)?/, '');
  return s.length > max ? s.slice(0, max) + '…' : s;
};
